import { useEffect, useMemo, useState } from 'react';

import { Entity } from '@backstage/catalog-model';
import { Select, SelectedItems } from '@backstage/core-components';
import {
  DefaultEntityFilters,
  EntityFilter,
  useEntityList,
  useEntityOwnership,
} from '@backstage/plugin-catalog-react';

import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';

import { useApplications } from '../../hooks';
import { Application, FILTER_ALL_LABEL } from '../../types';
import { useStyles } from './styles';

const normalizeFilterValue = (value: string): string => value.toLowerCase();

const getTenantSpec = (entity: Entity): Record<string, any> =>
  (entity.spec?.tenant as Record<string, any> | undefined) ?? {};

export class StageAndTierFilter implements EntityFilter {
  constructor(readonly values: string[]) {}

  filterEntity(entity: Entity): boolean {
    const selected = this.values.map(normalizeFilterValue);
    if (selected?.[0] === 'all') {
      return true;
    }
    const { stage, tier } = getTenantSpec(entity);
    const current = normalizeFilterValue(`${stage} ${tier}`);
    return selected.includes(current);
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

export class OwnerOnlyFilter implements EntityFilter {
  constructor(private readonly isOwnedFn: (entity: Entity) => boolean) {}

  filterEntity(entity: Entity): boolean {
    return this.isOwnedFn(entity);
  }

  toQueryValue(): string[] {
    return ['owned'];
  }
}

export class RegionFilter implements EntityFilter {
  constructor(readonly values: string[]) {}

  filterEntity(entity: Entity): boolean {
    const selected = this.values.map(normalizeFilterValue);
    if (selected?.[0] === 'all') {
      return true;
    }
    const current = normalizeFilterValue(String(getTenantSpec(entity).clusterId ?? ''));
    return current.includes(selected?.[0]);
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

export class ClusterFilter implements EntityFilter {
  constructor(readonly values: string[]) {}

  filterEntity(entity: Entity): boolean {
    const selected = this.values.map(normalizeFilterValue);
    if (selected?.[0] === 'all') {
      return true;
    }
    const current = normalizeFilterValue(String(getTenantSpec(entity).clusterId ?? ''));
    return current.includes(selected?.[0]);
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

export class ApplicationFilter implements EntityFilter {
  constructor(readonly values: string[]) {}

  filterEntity(entity: Entity): boolean {
    const selected = this.values.map(normalizeFilterValue);
    if (selected?.[0] === 'all') {
      return true;
    }
    const current = normalizeFilterValue(
      String(getTenantSpec(entity).applicationId ?? ''),
    );
    return selected.includes(current);
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

export class AppPropertyFilter implements EntityFilter {
  constructor(
    readonly values: string[],
    private readonly applications: Application[],
    private readonly appProperty: 'ccname' | 'ouname' | 'buname',
  ) {}

  filterEntity(entity: Entity): boolean {
    if (this.values?.[0] === FILTER_ALL_LABEL) {
      return true;
    }
    const applicationId = normalizeFilterValue(
      String(getTenantSpec(entity).applicationId ?? ''),
    );
    const application = this.applications.find(
      app => app.id.toLowerCase() === applicationId,
    );
    if (!application) {
      return false;
    }
    return this.values.includes(application[this.appProperty]);
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

const getClustersFromEntities = (entities: Entity[]): string[] =>
  Array.from(
    new Set(
      entities.map(entity => {
        const parts = String(getTenantSpec(entity).clusterId ?? '').split('/');
        return parts.length === 2 ? parts[1] : '';
      }),
    ).values(),
  ).filter(Boolean);

const getRegionsFromClusters = (clusters: string[]): string[] =>
  Array.from(
    new Set(
      clusters.map(cluster => {
        const parts = cluster.split('-');
        return parts.length > 1 ? parts[1] : '';
      }),
    ).values(),
  ).filter(Boolean);

const uniqueSortedOptions = (values: string[]) =>
  Array.from(new Set(values))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .map(value => ({ label: value, value }));

const getOptions = (entities: Entity[], applications: Application[]) => {
  const clusters = getClustersFromEntities(entities);
  const regions = getRegionsFromClusters(clusters);

  return {
    stageAndTiers: [
      { label: 'All', value: 'All' },
      { label: 'Dev Playground', value: 'Dev Playground' },
      { label: 'Dev Internal', value: 'Dev Internal' },
      { label: 'Prod Internal', value: 'Prod Internal' },
      { label: 'Prod External', value: 'Prod External' },
    ],
    clusterOptions: [
      { label: 'All', value: 'All' },
      ...clusters.map(cluster => ({ label: cluster, value: cluster })),
    ],
    regionOptions: [
      { label: 'All', value: 'All' },
      ...regions.map(region => ({ label: region, value: region })),
    ],
    applicationOptions: [
      { label: 'All', value: 'All' },
      ...applications.map(application => ({
        label: application.name,
        value: application.id,
      })),
    ],
    costCenterOptions: [
      { label: 'All', value: 'All' },
      ...uniqueSortedOptions(applications.map(app => app.ccname)),
    ],
    orgUnitOptions: [
      { label: 'All', value: 'All' },
      ...uniqueSortedOptions(applications.map(app => app.ouname)),
    ],
    businessUnitOptions: [
      { label: 'All', value: 'All' },
      ...uniqueSortedOptions(applications.map(app => app.buname)),
    ],
  };
};

export type KaaPTenantEntityFilters = {
  ownedByMe?: OwnerOnlyFilter;
  stageAndTier?: StageAndTierFilter;
  region?: RegionFilter;
  cluster?: ClusterFilter;
  application?: ApplicationFilter;
  costCenter?: AppPropertyFilter;
  orgUnit?: AppPropertyFilter;
  businessUnit?: AppPropertyFilter;
} & DefaultEntityFilters;

export const KaaPTenantFilters = ({ isAdmin }: { isAdmin: boolean }) => {
  const classes = useStyles();
  const { updateFilters, backendEntities } =
    useEntityList<KaaPTenantEntityFilters>();
  const applications = useApplications(isAdmin);
  const options = useMemo(
    () => getOptions(backendEntities, applications),
    [backendEntities, applications],
  );
  const { isOwnedEntity } = useEntityOwnership();

  const [selectedStageAndTier, setSelectedStageAndTier] =
    useState<string>('All');
  const [selectedRegion, setSelectedRegion] = useState<string>('All');
  const [selectedCluster, setSelectedCluster] = useState<string>('All');
  const [selectedApplication, setSelectedApplication] = useState<string>('All');
  const [selectedCostCenter, setSelectedCostCenter] = useState<string>('All');
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<string>('All');
  const [selectedBusinessUnit, setSelectedBusinessUnit] =
    useState<string>('All');
  const [ownedOnly, setOwnedOnly] = useState<boolean>(true);

  useEffect(() => {
    updateFilters({
      ownedByMe: ownedOnly ? new OwnerOnlyFilter(isOwnedEntity) : undefined,
      stageAndTier: new StageAndTierFilter([selectedStageAndTier]),
      region: new RegionFilter([selectedRegion]),
      cluster: new ClusterFilter([selectedCluster]),
      application: new ApplicationFilter([selectedApplication]),
      costCenter: new AppPropertyFilter(
        [selectedCostCenter],
        applications,
        'ccname',
      ),
      orgUnit: new AppPropertyFilter([selectedOrgUnit], applications, 'ouname'),
      businessUnit: new AppPropertyFilter(
        [selectedBusinessUnit],
        applications,
        'buname',
      ),
    } as KaaPTenantEntityFilters);
  }, [
    selectedStageAndTier,
    selectedRegion,
    selectedCluster,
    selectedApplication,
    selectedCostCenter,
    selectedOrgUnit,
    selectedBusinessUnit,
    ownedOnly,
    applications,
    updateFilters,
    isOwnedEntity,
  ]);

  return (
    <Box className={classes.filtersPaperChildContainer}>
      <Typography className={classes.filtersTitle}>Filters</Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={ownedOnly}
            onChange={(_, checked) => setOwnedOnly(checked)}
            color="primary"
          />
        }
        label="Owned by me"
      />
      <FormControl fullWidth style={{ marginTop: 12 }}>
        <Select
          label="Stage & Tier"
          selected={selectedStageAndTier}
          onChange={(value: SelectedItems) =>
            setSelectedStageAndTier(String(value))
          }
          items={options.stageAndTiers}
        />
      </FormControl>
      <FormControl fullWidth style={{ marginTop: 12 }}>
        <Select
          label="Region"
          selected={selectedRegion}
          onChange={(value: SelectedItems) => setSelectedRegion(String(value))}
          items={options.regionOptions}
        />
      </FormControl>
      <FormControl fullWidth style={{ marginTop: 12 }}>
        <Select
          label="Cluster"
          selected={selectedCluster}
          onChange={(value: SelectedItems) => setSelectedCluster(String(value))}
          items={options.clusterOptions}
        />
      </FormControl>
      {isAdmin && (
        <>
          <FormControl fullWidth style={{ marginTop: 12 }}>
            <Select
              label="Application"
              selected={selectedApplication}
              onChange={(value: SelectedItems) =>
                setSelectedApplication(String(value))
              }
              items={options.applicationOptions}
            />
          </FormControl>
          <FormControl fullWidth style={{ marginTop: 12 }}>
            <Select
              label="Cost Center"
              selected={selectedCostCenter}
              onChange={(value: SelectedItems) =>
                setSelectedCostCenter(String(value))
              }
              items={options.costCenterOptions}
            />
          </FormControl>
          <FormControl fullWidth style={{ marginTop: 12 }}>
            <Select
              label="Org Unit"
              selected={selectedOrgUnit}
              onChange={(value: SelectedItems) =>
                setSelectedOrgUnit(String(value))
              }
              items={options.orgUnitOptions}
            />
          </FormControl>
          <FormControl fullWidth style={{ marginTop: 12 }}>
            <Select
              label="Business Unit"
              selected={selectedBusinessUnit}
              onChange={(value: SelectedItems) =>
                setSelectedBusinessUnit(String(value))
              }
              items={options.businessUnitOptions}
            />
          </FormControl>
        </>
      )}
    </Box>
  );
};
